import React, { type FC, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import {
	APPROVALS_ANALYTICS_SOURCE,
	computeReviewStatusCounts,
	getFailureStatusCode,
} from '../analytics.ts';
import { useApprovals, useApprovalsDispatch } from '../ApprovalsContext.tsx';
import { openApprovalsPaneForContext } from '../approvalsPaneSidebar.ts';
import { ApprovalActionConfirmationModal } from '../Modals/ApprovalActionConfirmationModal.tsx';
import { CancelApprovalModal } from '../Modals/CancelApprovalModal.tsx';
import { PressableLink } from '../PressableLink.tsx';

const i18n = defineMessages({
	approvalInProgressBannerPrefixImproved: {
		id: 'approvals.editor.approval-in-progress.banner.prefix.improved',
		description:
			'Banner message when user is on edit page but approval is pending - editing is disabled.',
		defaultMessage: 'Editing is locked while an approval is pending.',
	},
	approvalInProgressBannerPrefixInProgress: {
		id: 'approvals.editor.approval-in-progress.banner.prefix.in-progress',
		description:
			'Banner message when user is on edit page but approval is pending - editing is disabled.',
		defaultMessage: 'Editing is locked while an approval is in progress.',
	},
	approvalInProgressCancelCta: {
		id: 'approvals.editor.approval-in-progress.banner.cancel-cta',
		description:
			'CTA link to cancel the approval when editing is locked due to a pending approval.',
		defaultMessage: 'Cancel approval',
	},
	approvedBannerPrefixImproved: {
		id: 'approvals.editor.approved.banner.prefix.improved',
		description:
			'Banner message when user is on edit page but page is approved - editing is disabled.',
		defaultMessage: 'You cannot edit an approved page without clearing the approval.',
	},
	approvedClearCta: {
		id: 'approvals.editor.approved.banner.clear-cta',
		description: 'CTA link to clear the approval when editing is locked due to an approved review.',
		defaultMessage: 'Clear approval',
	},
	approvalRequiredDraftBannerPrefix: {
		id: 'approvals.editor.approval-required.banner.draft.prefix',
		description:
			'Banner message shown in the editor on a draft that has never been published, when the space requires an approval before the draft can be published.',
		defaultMessage: 'Approval is required before you can publish this draft.',
	},
	approvalRequiredPublishedBannerPrefix: {
		id: 'approvals.editor.approval-required.banner.published.prefix',
		description:
			'Banner message shown in the editor when editing an already-published page, where the space requires an approval before the page can be updated.',
		defaultMessage: 'Approval is required before you can update this page.',
	},
	approvalRequiredBlockedByPublishedReviewBannerPrefix: {
		id: 'approvals.editor.approval-required.banner.blocked-by-published-review.prefix',
		description:
			'Banner message shown in the editor when editing an already-published page that requires an approval before it can be updated, and an approval is already running on the published page so one cannot be started on these changes yet.',
		defaultMessage:
			'Approval is required to update this page. Complete or cancel the published page’s approval to start one here.',
	},
	approvalRequiredStartCta: {
		id: 'approvals.editor.approval-required.banner.start-cta',
		description:
			'CTA link that opens the approvals panel so the author can start an approval on the page they are editing.',
		defaultMessage: 'Start approval',
	},
});

const styles = cssMap({
	banner: {
		height: '40px',
		paddingBlock: token('space.050'),
		paddingInline: token('space.250'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

const TRIGGER = 'banner';
const APPROVAL_REQUIRED_TRIGGER = 'editorBanner';

export type ApprovalInProgressEditorBlockerProps = {
	/** Content type of the page being edited — required approval applies to pages only */
	contentType?: string;
	/** True when the draft has never been published, which changes the banner copy */
	isUnpublishedDraft?: boolean;
};

/**
 * When approval is in progress (isDraftLockedForEditing), shows a banner explaining that editing
 * is not allowed. Renders nothing when there is no approval in progress or when changes are
 * requested (DENIED status) — in that case editing is permitted.
 *
 * - PENDING: "Editing is locked while an approval is pending. Cancel approval"
 * - APPROVED: "You cannot edit an approved page without clearing the approval. Clear approval."
 * - DENIED / no lock: renders nothing
 *
 * Behind `confluence_approvals_entry_point_updates_exp`, the same strip also carries the
 * approval-required entry point when the space blocks publishing until the page is approved and
 * no review exists yet:
 *
 * - Unpublished draft: "Approval is required before you can publish this draft. Start approval"
 * - Published page:    "Approval is required before you can update this page. Start approval"
 *
 * While the published page itself is in review, an approval can't be started on these changes, so
 * the strip carries the copy without a CTA instead: "Approval is required to update this page.
 * Complete or cancel the published page's approval to start one here."
 *
 * The entry point disappears as soon as a review exists (pending, approved or changes requested)
 * and comes back only once the review is cleared.
 */
export const ApprovalInProgressEditorBlocker: FC<ApprovalInProgressEditorBlockerProps> = ({
	contentType,
	isUnpublishedDraft = false,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		isDraftLockedForEditing,
		approvalRequest,
		approvalRequestLoading,
		isCurrentUserRequestor,
		isCurrentUserApprover,
		isContentApprovalsEnabled,
		disallowFirstPublish,
		hasEditPermission,
		contentId,
		contentStatus,
		otherModeReviewStatus,
	} = useApprovals();
	const { cancelWorkflowApplication } = useApprovalsDispatch();
	const rightSidebarContext = useRightSidebarContext();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

	const analyticsAttributes = useMemo(() => {
		const approvers = approvalRequest?.review?.approvers ?? [];
		return {
			reviewId: approvalRequest?.review?.reviewId,
			applicationId: approvalRequest?.application?.applicationId,
			contentId,
			isRequestor: isCurrentUserRequestor,
			isApprover: isCurrentUserApprover,
			isReviewPending: approvalRequest?.review?.reviewStatus === 'PENDING',
			currentReviewStatus: computeReviewStatusCounts(approvers),
			trigger: TRIGGER,
		};
	}, [approvalRequest, contentId, isCurrentUserApprover, isCurrentUserRequestor]);

	const reviewStatus = approvalRequest?.review?.reviewStatus;
	const isPending = reviewStatus === 'PENDING';
	const modalVariant = isPending ? 'cancelApproval' : 'clearApproval';

	// Approvers who already responded, shown above the description in the cancel approval modal.
	const respondedApproverData = useMemo(
		() =>
			approvalRequest?.review?.approvers?.reduce<
				Array<{ name: string; src?: string; email?: string; userId?: string }>
			>((acc, approver) => {
				if (approver.status !== 'PENDING_REVIEW') {
					acc.push({
						name: approver.name,
						src: approver.avatarUrl,
						email: approver.email,
						userId: approver.principalType === 'USER' ? approver.principalId : undefined,
					});
				}
				return acc;
			}, []) ?? [],
		[approvalRequest?.review?.approvers],
	);

	const handleOpenConfirmModal = useCallback(() => {
		setIsConfirmModalOpen(true);
	}, []);

	const handleConfirm = useCallback(async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'cancelApproval',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: analyticsAttributes,
			},
		}).fire();
		setIsConfirmModalOpen(false);

		try {
			await cancelWorkflowApplication();
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'canceled',
					actionSubject: 'approvalWorkflowReview',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: analyticsAttributes,
				},
			}).fire();
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'failed',
					actionSubject: 'approvalWorkflowReview',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: {
						...analyticsAttributes,
						failureStatusCode: getFailureStatusCode(error),
					},
				},
			}).fire();
		}
	}, [analyticsAttributes, cancelWorkflowApplication, createAnalyticsEvent]);

	const handleClose = useCallback(() => {
		setIsConfirmModalOpen(false);
	}, []);

	const handleStartApproval = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'startApproval',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: {
					contentId,
					isUnpublishedDraft,
					trigger: APPROVAL_REQUIRED_TRIGGER,
				},
			},
		}).fire();

		// Take the content status straight from the provider rather than deriving it here: it is
		// `DRAFT` for the whole editor route (`pageMode === EDIT`), published page or not, because
		// the review attaches to the unpublished changes. That keeps the pane context key
		// (`contentId:status`) identical to the one the byline entry point opens on the same page.
		openApprovalsPaneForContext({
			contentId,
			contentStatus,
			entryPoint: APPROVAL_REQUIRED_TRIGGER,
			rightSidebarContext,
		});
	}, [contentId, contentStatus, createAnalyticsEvent, isUnpublishedDraft, rightSidebarContext]);

	if (!isDraftLockedForEditing) {
		// A review that exists in any non-canceled state owns the messaging (either the locked
		// banner above, or — for changes requested / approved — the byline banners).
		const hasReview = Boolean(approvalRequest?.review) && reviewStatus !== 'CANCELED';
		// Mirrors `usePreventFirstPublishWithoutApproval`: updates to an already-published page are
		// only blocked once every publish requires an approval, so the banner never promises a
		// restriction that publishing would not actually enforce.
		const isPublishBlockedByApprovalPolicy =
			isContentApprovalsEnabled &&
			contentType === 'page' &&
			disallowFirstPublish &&
			(isUnpublishedDraft ||
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained; mirrors usePreventFirstPublishWithoutApproval.
				fg('cc_approvals_require_approval_every_publish'));

		const shouldShowApprovalRequiredBanner =
			isPublishBlockedByApprovalPolicy &&
			!hasReview &&
			!approvalRequestLoading &&
			hasEditPermission &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
			expValEquals('confluence_approvals_entry_point_updates_exp', 'isEnabled', true);

		if (!shouldShowApprovalRequiredBanner) {
			return null;
		}

		// A page can't have an approval running on both versions at once, so while the published
		// page is in review an approval can't be started on these changes. Rather than offering a
		// link that would fail, the banner says what has to happen to the published page's
		// approval first. Same rule the byline uses before opening the pane.
		const canStartApproval = otherModeReviewStatus !== 'PENDING';
		// Only a published page can have a review on its other version, so a draft that was never
		// published keeps the plain sentence.
		const isBlockedByPublishedReview = !canStartApproval && !isUnpublishedDraft;

		let bannerMessage = i18n.approvalRequiredPublishedBannerPrefix;
		if (isBlockedByPublishedReview) {
			bannerMessage = i18n.approvalRequiredBlockedByPublishedReviewBannerPrefix;
		} else if (isUnpublishedDraft) {
			bannerMessage = i18n.approvalRequiredDraftBannerPrefix;
		}

		return (
			<Flex xcss={styles.banner} alignItems="center" justifyContent="center" gap="space.050">
				<Text>{formatMessage(bannerMessage)}</Text>
				{canStartApproval && (
					<PressableLink onClick={handleStartApproval}>
						{formatMessage(i18n.approvalRequiredStartCta)}
					</PressableLink>
				)}
			</Flex>
		);
	}

	return (
		<>
			{reviewStatus === 'APPROVED' ? (
				<Flex xcss={styles.banner} alignItems="center" justifyContent="center" gap="space.050">
					<Text>{formatMessage(i18n.approvedBannerPrefixImproved)}</Text>
					<PressableLink onClick={handleOpenConfirmModal}>
						{formatMessage(i18n.approvedClearCta)}
					</PressableLink>
				</Flex>
			) : (
				// reviewStatus === 'PENDING' (the only remaining locked state)
				<Flex xcss={styles.banner} alignItems="center" justifyContent="center" gap="space.050">
					<Text>
						{formatMessage(
							expValEquals('confluence_approvals_entry_point_updates_exp', 'isEnabled', true)
								? i18n.approvalInProgressBannerPrefixInProgress
								: i18n.approvalInProgressBannerPrefixImproved,
						)}
					</Text>
					<PressableLink onClick={handleOpenConfirmModal}>
						{formatMessage(i18n.approvalInProgressCancelCta)}
					</PressableLink>
				</Flex>
			)}
			{isConfirmModalOpen &&
				(isPending &&
				expValEquals('confluence_approvals_entry_point_updates_exp', 'isEnabled', true) ? (
					// Same single-action modal the approvals pane uses to cancel an approval.
					<CancelApprovalModal
						approverData={respondedApproverData}
						showProfileCards={respondedApproverData.some(({ userId }) => Boolean(userId))}
						onCancelApproval={() => void handleConfirm()}
						onClose={handleClose}
					/>
				) : (
					<ApprovalActionConfirmationModal
						variant={modalVariant}
						onConfirm={handleConfirm}
						onClose={handleClose}
					/>
				))}
		</>
	);
};
